import { isEmpty, escapeRegExp, last, flatten } from 'lodash-es';
import { h } from './index-34617187.js';
import { z as BXF_ASSISTIVE_TEXT_HIDDEN } from './bxf-utils-3908c3c5.js';
import { u as uuidv4 } from './uuidv4-06ebe3ff.js';

/**
 * Utility methods for rendering live regions for search combobox (e.g. typeahead, picklist)
 */
/**
 * Render live region with text only.
 */
function renderLiveRegionText(text) {
    return (h("span", { class: BXF_ASSISTIVE_TEXT_HIDDEN, "aria-live": 'polite' }, text));
}
/**
 * Get base result string given length
 */
function getResultsFoundText(resultsLen) {
    return `${resultsLen} result${resultsLen === 1 ? '' : 's'} found`;
}
/**
 * Given an array of data and a search string, return search text based
 * 	on how many items are in the dataset.
 */
function getTextForResults(data, searchString = '') {
    const resultsLen = data?.length || 0;
    const resultsFoundText = getResultsFoundText(resultsLen);
    const suffixText = !isEmpty(searchString) ? ` for ${searchString}` : '';
    return `${resultsFoundText}${suffixText}`;
}

class BxfTypeAheadMatch {
    startIndex;
    endIndex;
    /**
     * Holds an array of indices where a match has been found.
     * This only works with the dynamic highlight-mode.
     */
    dynamic;
    constructor(startIndex, endIndex) {
        this.startIndex = startIndex;
        this.endIndex = endIndex;
    }
}

class BxfTypeAheadUtil {
    static getOptionBranch(bxfId, data) {
        let optionGroup;
        let optionGroupIndex;
        let option;
        for (let index = 0; index < data.length; index++) {
            optionGroup = data[index];
            option = optionGroup.values.find((item) => item.bxfId === bxfId);
            if (option) {
                optionGroupIndex = index;
                break;
            }
        }
        return { optionGroup, optionGroupIndex, option };
    }
    static initIds(data) {
        if (isEmpty(data))
            return;
        data.forEach((optionGroup) => {
            if (isEmpty(optionGroup.bxfId)) {
                optionGroup.bxfId = uuidv4();
            }
            optionGroup.values.forEach((option) => {
                if (isEmpty(option.bxfId)) {
                    option.bxfId = uuidv4();
                }
            });
        });
    }
    static findMatchingCharacters(word, wordToMatch) {
        const alphabet = {};
        for (let i = 0; i < word.length; i++) {
            const character = word.charAt(i);
            const regex = new RegExp(escapeRegExp(character), 'gi');
            const indices = new Array();
            let result;
            while ((result = regex.exec(wordToMatch))) {
                indices.push(result.index);
            }
            if (!isEmpty(indices)) {
                if (!alphabet[character]) {
                    alphabet[character] = new Set();
                }
                indices.forEach((index) => alphabet[character].add(index));
            }
        }
        return alphabet;
    }
    static isSubSequence(word, wordToMatch) {
        const indices = [];
        for (let i = 0; i < word.length; i++) {
            const index = wordToMatch.indexOf(word[i], last(indices) + 1);
            if (index === -1) {
                continue;
            }
            if (i === 0) {
                indices.push(index);
            }
            else if (index !== -1) {
                indices.push(index);
            }
            else {
                break;
            }
        }
        if (indices.length < word.length)
            return [];
        return indices;
    }
    static findMatch(word, wordToMatch, type, valueToMatch = '', ignorePattern) {
        let match;
        if (word && wordToMatch) {
            const _word = ignorePattern
                ? word.toLowerCase().replace(ignorePattern, '')
                : word.toLowerCase();
            const _wordToMatch = wordToMatch.toLowerCase();
            const _valueToMatch = typeof valueToMatch === 'string'
                ? valueToMatch.toLowerCase()
                : '';
            switch (type) {
                case 'suggested':
                    match = this.findMatchSuggested(_word, _wordToMatch);
                    break;
                case 'popular':
                    match = this.findMatchPopular(_word, _wordToMatch);
                    break;
                case 'dynamic':
                    match = this.findMatchDynamic(_word, _wordToMatch, _valueToMatch);
                    break;
            }
        }
        return match;
    }
    static findMatchExact(word, wordToMatch, valueToMatch = '', ignorePattern) {
        if (word && wordToMatch) {
            const _word = ignorePattern
                ? word.toLowerCase().replace(ignorePattern, '')
                : word.toLowerCase();
            const _wordToMatch = wordToMatch.toLowerCase();
            const _valueToMatch = typeof valueToMatch === 'string'
                ? valueToMatch.toLowerCase()
                : '';
            return _word === _wordToMatch || _word === _valueToMatch;
        }
        return false;
    }
    static findMatchDynamic(word, wordToMatch, valueToMatch) {
        const valueSequence = BxfTypeAheadUtil.isSubSequence(word, valueToMatch);
        const wordSequence = BxfTypeAheadUtil.isSubSequence(word, wordToMatch);
        let match;
        if (isEmpty(valueSequence)) {
            match = wordSequence;
        }
        else if (isEmpty(wordSequence)) {
            // Match everything found in value or word match
            match = flatten(Object.values(this.findMatchingCharacters(word, wordToMatch + valueToMatch)).map((c) => Array.from(c)));
        }
        else {
            match = wordSequence;
        }
        if (isEmpty(match))
            return;
        return {
            startIndex: -1,
            endIndex: -1,
            dynamic: match,
        };
    }
    static findMatchSuggested(word, wordToMatch) {
        let match;
        const indexOf = wordToMatch.indexOf(word);
        if (indexOf === 0) {
            match = new BxfTypeAheadMatch(indexOf, word.length);
        }
        return match;
    }
    static findMatchPopular(word, wordToMatch) {
        let match;
        const indexOfMatch = wordToMatch.indexOf(word);
        if (indexOfMatch >= 0) {
            match = new BxfTypeAheadMatch(indexOfMatch, indexOfMatch + word.length);
        }
        return match;
    }
}

export { BxfTypeAheadMatch as B, BxfTypeAheadUtil as a, getTextForResults as b, getResultsFoundText as g, renderLiveRegionText as r };
